const API = import.meta.env.VITE_API_URL || '/api'

function buildQuery(params = {}) {
  if (!params || typeof params !== 'object') return ''
  const clean = {}
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '' && v !== 'undefined' && v !== 'null') {
      clean[k] = v
    }
  }
  const qs = new URLSearchParams(clean).toString()
  return qs ? `?${qs}` : ''
}

async function req(path, options = {}) {
  const token = localStorage.getItem('token')
  const isFormData = options.body instanceof FormData
  const headers = { 
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }), 
    ...(token ? { Authorization: `Bearer ${token}` } : {}), 
    ...options.headers 
  }
  const res = await fetch(`${API}/masterclass${path}`, { ...options, headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || 'Request failed')
  return data
}

export const masterclassService = {
  getUpcoming: () => req('/upcoming'),
  getPast: () => req('/past'),
  getArchive: (params = {}) => req(`/archive${buildQuery(params)}`),
  getCategories: () => req('/categories'),
  getYears: () => req('/years'),
  getSpeakers: () => req('/speakers'),
  getSpeaker: (id) => req(`/speakers/${id}`),
  getMasterclass: (id) => req(`/${id}`),
  getMasterclassDetail: (slug) => req(`/detail/${slug}`),
  getGallery: (params = {}) => req(`/gallery${buildQuery(params)}`),
  getVideos: (params = {}) => req(`/videos${buildQuery(params)}`),
  getTestimonials: (params = {}) => req(`/testimonials${buildQuery(params)}`),
  getResources: (params = {}) => req(`/resources${buildQuery(params)}`),
  getStats: () => req('/stats'),
  getHomepageSettings: () => req('/homepage'),
  getPaymentConfig: () => req('/payment-config'),
  verifyRegistration: (regNum) => req(`/verify/${regNum}`),
  getPassInfo: (passToken) => req(`/pass/${passToken}`),
  checkInByPass: (passToken, options = {}) => req(`/pass/${passToken}/check-in`, { method: 'POST', body: JSON.stringify(options) }),
  register: (body) => req('/register', { method: 'POST', body: JSON.stringify(body) }),
  studentRegister: (body) => req('/student-register', { method: 'POST', body: JSON.stringify(body) }),
  payMasterclassGuestPass: (body) => req('/pay', { method: 'POST', body: JSON.stringify(body) }),
  submitPaymentProof: (formDataOrBody, proofSubmissionToken) => {
    const headers = proofSubmissionToken ? { 'X-Registration-Proof-Token': proofSubmissionToken } : {}
    if (formDataOrBody instanceof FormData) {
      return req('/submit-proof', { method: 'POST', body: formDataOrBody, headers })
    }
    return req('/submit-proof', { method: 'POST', body: JSON.stringify(formDataOrBody), headers })
  },

  // Admin
  adminGetRegistrations: (params = {}) => req(`/admin/registrations${buildQuery(params)}`),
  adminDeleteRegistration: (id) => req(`/admin/registrations/${id}`, { method: 'DELETE' }),
  adminRestoreRegistration: (id) => req(`/admin/registrations/${id}/restore`, { method: 'POST' }),
  adminPermanentDeletePreview: (id) => req(`/admin/registrations/${id}/permanent-preview`),
  adminPermanentDeleteRegistration: (id) => req(`/admin/registrations/${id}/permanent`, { method: 'DELETE' }),
  adminGetPendingVerifications: (params = {}) => req(`/admin/pending-verifications${buildQuery(params)}`),
  adminGetProofUrl: (registrationId) => req(`/registrations/${registrationId}/proof-url`),
  adminVerifyPayment: (body) => req('/admin/verify-payment', { method: 'POST', body: JSON.stringify(body) }),
  adminRejectPayment: (body) => req('/admin/reject-payment', { method: 'POST', body: JSON.stringify(body) }),
  adminCheckIn: (registration_number, options = {}) => req('/admin/checkin', { method: 'POST', body: JSON.stringify({ registration_number, ...options }) }),
  adminGetStats: (masterclass_id) => req(`/admin/stats${buildQuery({ masterclass_id })}`),
  adminGetMasterclasses: () => req('/admin/masterclasses'),
  adminGetLastFriday: (year, month) => req(`/admin/last-friday${buildQuery({ year, month })}`),
  adminCreateMasterclass: (body) => req('/admin/masterclasses', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateMasterclass: (id, body) => req(`/admin/masterclasses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminCreateSpeaker: (body) => req('/admin/speakers', { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) }),
  adminUpdateSpeaker: (id, body) => req(`/admin/speakers/${id}`, { method: 'PUT', body: body instanceof FormData ? body : JSON.stringify(body) }),
  adminDeleteSpeaker: (id) => req(`/admin/speakers/${id}`, { method: 'DELETE' }),
  adminExport: (masterclass_id) => req(`/admin/export${buildQuery({ masterclass_id })}`),
  adminSaveTakeaways: (body) => req('/admin/takeaways', { method: 'POST', body: JSON.stringify(body) }),
  adminCreateGallery: (body) => req('/admin/gallery', { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) }),
  adminDeleteGallery: (id) => req(`/admin/gallery/${id}`, { method: 'DELETE' }),
  adminCreateVideo: (body) => req('/admin/videos', { method: 'POST', body: JSON.stringify(body) }),
  adminDeleteVideo: (id) => req(`/admin/videos/${id}`, { method: 'DELETE' }),
  adminCreateTestimonial: (body) => req('/admin/testimonials', { method: 'POST', body: JSON.stringify(body) }),
  adminApproveTestimonial: (id, body) => req(`/admin/testimonials/${id}/approve`, { method: 'PUT', body: JSON.stringify(body) }),
  adminDeleteTestimonial: (id) => req(`/admin/testimonials/${id}`, { method: 'DELETE' }),
  adminCreateResource: (body) => req('/admin/resources', { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) }),
  adminDeleteResource: (id) => req(`/admin/resources/${id}`, { method: 'DELETE' }),
  adminGetHomepageSettings: () => req('/admin/homepage'),
  adminSaveHomepageSettings: (body) => req('/admin/homepage', { method: 'PUT', body: JSON.stringify(body) }),
  adminSendReminders: (window) => req('/admin/send-reminders', { method: 'POST', body: JSON.stringify({ window }) }),
  adminGetAttendanceReport: (masterclassId) => req(`/admin/attendance-report/${masterclassId}`),
  adminGetAttendanceCertificates: (params = {}) => req(`/admin/attendance-certificates${buildQuery(params)}`),
  adminGetCertificates: (params = {}) => req(`/admin/certificates${buildQuery(params)}`),
  adminRegenerateCertificate: (certNumber) => req(`/admin/certificate/${certNumber}/regenerate`, { method: 'POST' }),
  adminGetAnalytics: (params = {}) => req(`/admin/analytics${buildQuery(params)}`),
  adminGetTopicLibrary: () => req('/admin/topics/library'),
  adminDownloadTopicsPdf: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/masterclass/admin/topics/download-pdf`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error('Failed to download Topics Catalogue PDF');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'SMA-Masterclass-Topics-Catalogue.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
  adminCloneMasterclass: (id, body) => req(`/admin/clone/${id}`, { method: 'POST', body: JSON.stringify(body) }),

  // ── Flyer generation (1080x1080 PNG) ───────────────────────────────────
  // Admin: download flyer for a saved masterclass (auth required).
  adminDownloadFlyer: async (id, options = {}) => {
    const token = localStorage.getItem('token')
    const qs = buildQuery({ layout: options.layout, mode: options.mode, download: '1' })
    const res = await fetch(`${API}/masterclass/admin/masterclasses/${id}/flyer${qs}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.details ? `${data.error}: ${data.details}` : (data.error || 'Failed to download flyer'))
    }
    const blob = await res.blob()
    const layout = res.headers.get('X-Flyer-Layout') || 'A'
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SMA-Masterclass-Flyer-${id}-${layout}.png`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  // Public: download flyer for a masterclass by id or slug (no auth).
  // Used by the marketing page.
  publicDownloadFlyer: async (idOrSlug, options = {}) => {
    const qs = buildQuery({ layout: options.layout, mode: options.mode, download: '1' })
    const res = await fetch(`${API}/masterclass/${idOrSlug}/flyer${qs}`)
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Failed to download flyer')
    }
    const blob = await res.blob()
    const layout = res.headers.get('X-Flyer-Layout') || 'A'
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SMA-Masterclass-Flyer-${idOrSlug}-${layout}.png`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  // Admin: preview a specific layout + font for a saved masterclass (returns blob URL).
  // Used by the Regenerate Flyer comparison UI.
  adminPreviewFlyerLayout: async (id, layout, fontPairing) => {
    const token = localStorage.getItem('token')
    const qs = buildQuery({ layout, font: fontPairing })
    const res = await fetch(`${API}/masterclass/admin/masterclasses/${id}/flyer${qs}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.details ? `${data.error}: ${data.details}` : (data.error || 'Failed to preview flyer'))
    }
    const blob = await res.blob()
    const layoutUsed = res.headers.get('X-Flyer-Layout') || layout
    const fontUsed = res.headers.get('X-Flyer-Font') || fontPairing
    return { blobUrl: window.URL.createObjectURL(blob), layout: layoutUsed, fontPairing: fontUsed }
  },

  // Admin: generate all 12 layout previews at once (returns JSON with base64 PNGs).
  adminGetAllFlyerLayouts: async (id) => {
    const token = localStorage.getItem('token')
    const res = await fetch(`${API}/masterclass/admin/masterclasses/${id}/flyer-layouts`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.details ? `${data.error}: ${data.details}` : (data.error || 'Failed to generate flyer previews'))
    }
    return res.json()
  },

  // Admin: persist the selected flyer template + font pairing for a masterclass.
  adminSaveFlyerTemplate: async (id, layout, fontPairing) => {
    const token = localStorage.getItem('token')
    const res = await fetch(`${API}/masterclass/admin/masterclasses/${id}/flyer-template`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ layout, fontPairing }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.details ? `${data.error}: ${data.details}` : (data.error || 'Failed to save flyer template'))
    }
    return res.json()
  },

  // Student experience
  getMyLibrary: () => req('/my-library'),
  getMyCertificates: () => req('/my-certificates'),
  getMyBadges: () => req('/my-badges'),
  getMyAttendance: () => req('/my-attendance'),
  getMyAttendanceCertificates: () => req('/my-attendance-certificates'),
  addBookmark: (body) => req('/my-bookmarks', { method: 'POST', body: JSON.stringify(body) }),
  deleteBookmark: (id) => req(`/my-bookmarks/${id}`, { method: 'DELETE' }),
  trackDownload: (resourceId) => req('/track-download', { method: 'POST', body: JSON.stringify({ resource_id: resourceId }) }),
  getLeaderboard: (limit = 20) => req(`/leaderboard?limit=${limit}`),
  verifyCertificate: (number) => req(`/verify-certificate/${encodeURIComponent(number)}`),

  // ── Funds Ledger ────────────────────────────────────────────────────────
  adminGetFunds: (params = {}) => req(`/funds${buildQuery(params)}`),
  adminCreateFund: (body) => req('/funds', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateFund: (id, body) => req(`/funds/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminDeleteFund: (id) => req(`/funds/${id}`, { method: 'DELETE' }),
  adminGetMasterclassFunds: (id) => req(`/funds/masterclass/${id}`),
}
